import React, { useEffect, useState } from 'react';
import './SelectScaMethod.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageLayout from './common/PageLayout';
import {
    getAuthSession,
    updateActiveElement,
    initiateAuthorization,
    redirectToBank,
} from '../utils/authSession';
import { getScaMethodLabel, isBankIdMethod } from '../utils/scaMethodLabels';
import BankIdIcon from '../assets/icons/bankid';

// Step 2a — the user picks which SCA method to authenticate with. The available
// methods were cached on the authorisation when the consent was created (POST
// /consents never returns them again from the initiate-authorisation call).
function SelectScaMethod() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const session = getAuthSession();
    const activeElement = session?.list?.[session.currentIndex] || null;
    // Banks can expose several variants of the same method (e.g. mbid_animated_qr_token
    // and mbid_animated_qr_image both label as "BankID on another device"). Showing both
    // reads as a duplicate — keep only the first method per resolved label.
    const seenLabels = new Set();
    const methods = (activeElement?.scaMethods || []).filter((method) => {
        const label = getScaMethodLabel(method, t);
        if (seenLabels.has(label)) {
            return false;
        }
        seenLabels.add(label);
        return true;
    });
    const emailAvailable = !!activeElement?.emailAvailable;

    const [selectedMethodId, setSelectedMethodId] = useState(null);
    // 'browser' (authenticate here) or 'email' (send a signing link). In-browser is
    // always available; email only when the authoriser has an address on file.
    const [delivery, setDelivery] = useState('browser');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // No active authorisation in storage — the session was lost; start over.
    useEffect(() => {
        if (!activeElement) {
            navigate('/aspspsList');
        }
    }, [activeElement, navigate]);

    if (!activeElement) {
        return null;
    }

    const methodLabel = (method) => getScaMethodLabel(method, t);

    const useEmail = emailAvailable && delivery === 'email';
    const canProceed = !isSubmitting && (useEmail || !!selectedMethodId);

    const handleNext = async () => {
        if (!canProceed) {
            return;
        }

        // Out-of-band: a signing link is sent to the authoriser; nothing to do in
        // this browser. The coordinator (/sign-status) tracks completion.
        if (useEmail) {
            updateActiveElement({ emailChosen: true });
            navigate('/sign-status');
            return;
        }

        setIsSubmitting(true);
        setError('');
        try {
            const responseArray = await initiateAuthorization({
                consentId: session.consentId,
                authorizationIds: [activeElement.authorizationId],
                authMethod: selectedMethodId,
                createInputs: session.createInputs,
            });

            const fresh = responseArray.find(r => r.authorizationId === activeElement.authorizationId)
                || responseArray[0]
                || {};

            // The authorisation may be re-issued with a new id; keep the cached scaMethods.
            updateActiveElement({
                chosenMethod: selectedMethodId,
                authorizationId: fresh.authorizationId || activeElement.authorizationId,
                challengeData: fresh.challengeData ?? null,
                scaOAuthLink: fresh.scaOAuthLink ?? null,
                scaStatus: fresh.scaStatus ?? activeElement.scaStatus,
            });

            // ScaApproach indicates the authentication flow:
            // REDIRECT → hand off to the bank OAuth (scaOAuthLink). It returns the user to
            // /bank-connected to activate. DECOUPLED → show the QR and poll at /bank-connection.
            if (fresh.scaOAuthLink) {
                redirectToBank(fresh.scaOAuthLink);
                return;
            }
            navigate('/bank-connection');
        } catch (err) {
            console.error('Error initiating authorisation:', err);
            setError(t('selectMethodError', 'Something went wrong. Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const navigationProps = {
        previousPath: '/aspspsList',
        onNext: handleNext,
        nextDisabled: !canProceed,
    };

    return (
        <PageLayout currentStep={3} navigationProps={navigationProps} infoText="selectMethodInfoText">
            <div className="select-method-content">
                <h1 className="main-title">{t('selectMethodTitle', 'Choose authentication method')}</h1>
                <p className="main-subtitle">{t('selectMethodSubtitle', 'Select how you want to verify your identity with your bank.')}</p>

                {emailAvailable && (
                    <div className="delivery-options">
                        <label className={`delivery-option ${delivery === 'browser' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="delivery"
                                value="browser"
                                checked={delivery === 'browser'}
                                onChange={() => setDelivery('browser')}
                            />
                            <span>{t('selectMethodDeliveryBrowser', 'Authenticate in this browser')}</span>
                        </label>
                        <label className={`delivery-option ${delivery === 'email' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="delivery"
                                value="email"
                                checked={delivery === 'email'}
                                onChange={() => setDelivery('email')}
                            />
                            <span>{t('selectMethodDeliveryEmail', 'Send a signing link by email')}</span>
                        </label>
                    </div>
                )}

                {!useEmail && (
                    <div className="method-list">
                        {methods.length === 0 ? (
                            <div className="method-empty">{t('selectMethodNoMethods', 'No authentication methods available.')}</div>
                        ) : (
                            methods.map((method) => (
                                <label
                                    key={method.authenticationMethodId}
                                    className={`method-item ${selectedMethodId === method.authenticationMethodId ? 'selected' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        name="scaMethod"
                                        value={method.authenticationMethodId}
                                        checked={selectedMethodId === method.authenticationMethodId}
                                        onChange={() => setSelectedMethodId(method.authenticationMethodId)}
                                    />
                                    <div className="method-content">
                                        {isBankIdMethod(method) && (
                                            <BankIdIcon />
                                        )}
                                        <span className="method-name">{methodLabel(method)}</span>
                                    </div>
                                </label>
                            ))
                        )}
                    </div>
                )}

                {isSubmitting && (
                    <div className="select-method-submitting">{t('selectMethodSubmitting', 'Starting authentication…')}</div>
                )}

                {error && (
                    <div className="select-method-error" role="alert">{error}</div>
                )}
            </div>
        </PageLayout>
    );
}

export default SelectScaMethod;
